import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CssBaseline from '@mui/material/CssBaseline';
import AddIcon from '@mui/icons-material/Add';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ScienceIcon from '@mui/icons-material/Science';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import BuildIcon from '@mui/icons-material/Build';
import AppNavbar from '../dashboard/components/AppNavbar';
import SideMenu from '../dashboard/components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { DataModeProvider, DATA_MODES } from '../context/DataModeContext';
import { GrowProvider } from '../dashboard/context/GrowContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// Demo tasks data
const DEMO_TASKS = [
  {
    id: 1,
    title: 'Check pH levels',
    description: 'Reservoir pH trending high - needs adjustment',
    type: 'alert',
    priority: 'high',
    dueDate: new Date(),
    growName: 'Buttercrunch Lettuce',
    icon: <ScienceIcon />,
    completed: false,
  },
  {
    id: 2,
    title: 'Feed nutrients',
    description: 'Weekly feeding schedule - Week 4 Vegetative',
    type: 'reminder',
    priority: 'medium',
    dueDate: new Date(Date.now() + 2 * 60 * 60 * 1000),
    growName: 'Buttercrunch Lettuce',
    icon: <WaterDropIcon />,
    completed: false,
  },
  {
    id: 3,
    title: 'Clean reservoir',
    description: 'Monthly deep clean due',
    type: 'reminder',
    priority: 'low',
    dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
    growName: 'All Grows',
    icon: <CleaningServicesIcon />,
    completed: false,
  },
  {
    id: 4,
    title: 'Replace air stones',
    description: 'Air stones showing reduced output',
    type: 'maintenance',
    priority: 'medium',
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    growName: 'Hydro Setup',
    icon: <BuildIcon />,
    completed: false,
  },
  {
    id: 5,
    title: 'Calibrate EC meter',
    description: 'Monthly calibration completed',
    type: 'maintenance',
    priority: 'low',
    dueDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
    growName: 'All Grows',
    icon: <ScienceIcon />,
    completed: true,
  },
];

function TaskCard({ task, onToggle, onDelete }) {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'alert': return <WarningIcon sx={{ color: 'error.main' }} />;
      case 'reminder': return <NotificationsActiveIcon sx={{ color: 'primary.main' }} />;
      default: return <BuildIcon sx={{ color: 'text.secondary' }} />;
    }
  };

  const formatDueDate = (date) => {
    const now = new Date();
    const diff = date - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (diff < 0) return 'Overdue';
    if (hours < 1) return 'Due now';
    if (hours < 24) return `In ${hours} hour${hours > 1 ? 's' : ''}`;
    return `In ${days} day${days > 1 ? 's' : ''}`;
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        opacity: task.completed ? 0.6 : 1,
        bgcolor: task.completed ? 'action.hover' : 'background.paper',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Checkbox
          checked={task.completed}
          onChange={() => onToggle(task.id)}
          sx={{ mt: -0.5 }}
        />
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontFamily: 'Inter, sans-serif',
                  textDecoration: task.completed ? 'line-through' : 'none',
                }}
              >
                {task.title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {task.description}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {getTypeIcon(task.type)}
              <IconButton size="small" onClick={() => onDelete(task.id)}>
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
            <Chip label={task.growName} size="small" variant="outlined" />
            <Chip
              label={task.priority}
              size="small"
              color={getPriorityColor(task.priority)}
              sx={{ textTransform: 'capitalize' }}
            />
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography
                variant="caption"
                sx={{
                  color: task.dueDate < new Date() && !task.completed ? 'error.main' : 'text.secondary',
                  fontWeight: task.dueDate < new Date() && !task.completed ? 600 : 400,
                }}
              >
                {formatDueDate(task.dueDate)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
}

function TasksContent() {
  const [activeTab, setActiveTab] = React.useState(0);
  const [tasks, setTasks] = React.useState(DEMO_TASKS);

  const handleToggle = (taskId) => {
    setTasks(tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t));
  };

  const handleDelete = (taskId) => {
    setTasks(tasks.filter(t => t.id !== taskId));
  };

  const activeTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);
  const alertTasks = tasks.filter(t => t.type === 'alert' && !t.completed);

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1000px' }, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
            Tasks & Reminders
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Stay on top of your grow maintenance
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          New Reminder
        </Button>
      </Stack>

      {/* Alert Banner */}
      {alertTasks.length > 0 && (
        <Card
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: alpha('#ef4444', 0.1),
            borderColor: 'error.main',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <WarningIcon sx={{ color: 'error.main' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
                {alertTasks.length} Alert{alertTasks.length > 1 ? 's' : ''} Requiring Attention
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Sensor readings outside optimal ranges detected
              </Typography>
            </Box>
          </Stack>
        </Card>
      )}

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
            {activeTasks.length}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Pending</Typography>
        </Card>
        <Card variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
            {alertTasks.length}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Alerts</Typography>
        </Card>
        <Card variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
            {completedTasks.length}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Completed</Typography>
        </Card>
      </Stack>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Active (${activeTasks.length})`} sx={{ textTransform: 'none' }} />
        <Tab label={`Completed (${completedTasks.length})`} sx={{ textTransform: 'none' }} />
        <Tab label="All" sx={{ textTransform: 'none' }} />
      </Tabs>

      {/* Task List */}
      <Stack spacing={2}>
        {(activeTab === 0 ? activeTasks : activeTab === 1 ? completedTasks : tasks).map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onToggle={handleToggle}
            onDelete={handleDelete}
          />
        ))}
        {(activeTab === 0 ? activeTasks : activeTab === 1 ? completedTasks : tasks).length === 0 && (
          <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {activeTab === 0 ? 'All caught up! No pending tasks.' : 'No tasks to show.'}
            </Typography>
          </Card>
        )}
      </Stack>
    </Box>
  );
}

export default function TasksPage(props) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <DataModeProvider mode={isDemo ? DATA_MODES.DEMO : DATA_MODES.LIVE}>
          <GrowProvider>
            <SideMenu />
            <AppNavbar />
            <Box
              component="main"
              sx={(theme) => ({
                flexGrow: 1,
                backgroundColor: theme.vars
                  ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                  : alpha(theme.palette.background.default, 1),
                overflow: 'auto',
                minHeight: '100vh',
              })}
            >
              <Stack
                sx={{
                  pt: { xs: 10, md: 3 },
                  pb: 5,
                }}
              >
                <TasksContent />
              </Stack>
            </Box>
          </GrowProvider>
        </DataModeProvider>
      </Box>
    </AppTheme>
  );
}
